import type { PortalDiscoverySnapshotDto } from "../model";
import { portalFetch } from "./client";

export const DISCOVERY_EVENT_CATEGORIES = ["discovery.changed", "conversation.run.changed"] as const;
export type DiscoveryEventCategory = (typeof DISCOVERY_EVENT_CATEGORIES)[number];
export interface DiscoveryInvalidationEvent {
	projectId: string;
	mapId: string;
	revision: number;
	category: DiscoveryEventCategory;
	resourceHints: string[];
}

export type DiscoverySyncTelemetry =
	| { signal: "streamConnected" | "streamRetry"; retryCount: number }
	| { signal: "revisionApplied"; revision: number; emittedRevision: number }
	| { signal: "refreshCoalesced" }
	| { signal: "degradedEnded"; durationMs: number };

/** Best-effort and content-free: synchronization telemetry never affects Discovery access. */
export function reportDiscoverySyncTelemetry(projectId: string, mapId: string, telemetry: DiscoverySyncTelemetry) {
	void portalFetch<Record<string, never>>(
		`/api/portal/v1/projects/${projectId}/discovery-telemetry?mapId=${encodeURIComponent(mapId)}`,
		{ method: "POST", body: JSON.stringify(telemetry) },
	).catch(() => undefined);
}

export function shouldApplyDiscoverySnapshot(latestAppliedRevision: number | undefined, incomingRevision: number) {
	return latestAppliedRevision === undefined || incomingRevision > latestAppliedRevision;
}

export function fetchDiscoverySnapshot(projectId: string, mapId: string) {
	return portalFetch<PortalDiscoverySnapshotDto>(
		`/api/portal/v1/projects/${projectId}/discovery-snapshot?mapId=${encodeURIComponent(mapId)}`,
	);
}

export function discoveryEventsUrl(projectId: string, mapId: string) {
	return `${import.meta.env.VITE_SUBSTRATE_PORTAL_API_BASE ?? ""}/api/portal/v1/projects/${projectId}/discovery-events?mapId=${encodeURIComponent(mapId)}`;
}

export function parseDiscoveryInvalidation(data: string): DiscoveryInvalidationEvent | null {
	try {
		const value = JSON.parse(data) as Record<string, unknown>;
		const keys = Object.keys(value).sort();
		if (
			keys.join(",") !== "category,mapId,projectId,resourceHints,revision" ||
			typeof value.projectId !== "string" ||
			typeof value.mapId !== "string" ||
			typeof value.revision !== "number" ||
			!Number.isSafeInteger(value.revision) ||
			!DISCOVERY_EVENT_CATEGORIES.includes(value.category as DiscoveryEventCategory) ||
			!Array.isArray(value.resourceHints) ||
			!value.resourceHints.every((hint) => typeof hint === "string")
		)
			return null;
		return value as unknown as DiscoveryInvalidationEvent;
	} catch {
		return null;
	}
}

/** Serializes authoritative reads while retaining at most one required follow-up at a time. */
export class DiscoveryRefreshCoordinator {
	private active: Promise<void> | undefined;
	private followUpForced = false;
	private followUpRevision: number | undefined;

	constructor(
		private readonly refresh: () => Promise<number>,
		private readonly latestAppliedRevision: () => number | undefined,
		private readonly onCoalesced: () => void = () => undefined,
	) {}

	/** An omitted revision forces a refresh for lifecycle events and successful local mutations. */
	request(minimumRevision?: number): Promise<void> {
		if (minimumRevision !== undefined && minimumRevision <= (this.latestAppliedRevision() ?? -1))
			return this.active ?? Promise.resolve();
		if (this.active) {
			this.onCoalesced();
			if (minimumRevision === undefined) this.followUpForced = true;
			else this.followUpRevision = Math.max(this.followUpRevision ?? -1, minimumRevision);
			return this.active;
		}
		// This new read covers any work retained from an earlier failed read.
		this.followUpForced = false;
		this.followUpRevision = undefined;
		const run = async () => {
			await this.refresh();
			while (this.followUpForced || (this.followUpRevision ?? -1) > (this.latestAppliedRevision() ?? -1)) {
				this.followUpForced = false;
				this.followUpRevision = undefined;
				await this.refresh();
			}
			this.followUpRevision = undefined;
		};
		this.active = run().finally(() => {
			this.active = undefined;
		});
		return this.active;
	}
}
